import { DestroyRef, Directive, ElementRef, Injector, computed, effect, inject, input } from '@angular/core';
import { BrnTooltip } from '@spartan-ng/brain/tooltip';
import { OnboardingService } from './onboarding.service';

/**
 * Глушит `hlmTooltip` хоста, пока `silent()`; потом возвращает ему прежнее состояние. Флаг читается
 * отслеживаемо: эффект `HlmTooltip` пишет его сам, и порядок эффектов зависит от порядка `imports`.
 */
function muteTooltipWhile(silent: () => boolean): void {
  const tooltip = inject(BrnTooltip, { self: true, optional: true });
  if (!tooltip) return;

  let restore: boolean | null = null;
  effect(() => {
    const mute = silent();
    const disabled = tooltip.mutableTooltipDisabled();
    if (mute) {
      restore ??= disabled;
      if (!disabled) tooltip.mutableTooltipDisabled.set(true);
    } else if (restore !== null) {
      tooltip.mutableTooltipDisabled.set(restore);
      restore = null;
    }
  });
}

/**
 * Marks the host element as the anchor of an onboarding record. The page renders it only while its
 * own state fits — `null` means «no anchor here», and the engine never reads page state itself.
 *
 * Движок резолвится лениво: директива висит и на общих списках (пункты меню), где запись назначена
 * одному пункту из десяти, — остальным хостам порт онбординга знать незачем.
 *
 * ЗАКОН НАЖАТИЯ: карточку с роликом открывает только нажатие на «?» (`OnboardingHelpDirective`), наведение
 * — никогда: ни якорь, ни вход. Исключение — точечное, по слову продукта: `OnboardingHoverHelpDirective`.
 */
@Directive({ selector: '[onboardingAnchor]' })
export class OnboardingAnchorDirective {
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  readonly onboardingAnchor = input<string | null>(null);

  constructor() {
    effect(onCleanup => {
      const id = this.onboardingAnchor();
      if (!id) return;

      const onboarding = this.injector.get(OnboardingService);
      onboarding.registerAnchor(id, this.element);
      onCleanup(() => onboarding.unregisterAnchor(id, this.element));
    });
  }
}

/**
 * Вход в закрытый контейнер (кебаб строки, триггер меню или поповера), внутри которого висит якорь
 * записи.
 *
 * ЗАКОН, по умолчанию для любой записи каталога, чей якорь живёт внутри раскрываемого контейнера
 * (R44): `onboardingReveals` ставится на ВХОД, `onboardingAnchor` — на финальный элемент. Карточка
 * стоит на ВХОДЕ, пока вход зарегистрирован, — меню за фотографа движок не раскрывает никогда, и
 * открытое им самим меню карточку ни поднимает, ни переносит на пункт: вход перевешивает якорь
 * внутри, и карточка не закрывает собой раскрытое меню. Контейнер без перекрытия (раздел
 * аккордеона) снимает привязку входа сам — тогда карточка переезжает на якорь обычным порядком.
 */
@Directive({ selector: '[onboardingReveals]' })
export class OnboardingRevealDirective {
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  readonly onboardingReveals = input<string | null>(null);

  constructor() {
    effect(onCleanup => {
      const id = this.onboardingReveals();
      if (!id) return;

      const onboarding = this.injector.get(OnboardingService);
      onboarding.registerReveal(id, this.element);
      onCleanup(() => onboarding.unregisterReveal(id, this.element));
    });
  }
}

/**
 * Знак вопроса (или любая другая иконка помощи) у места, о котором коуч уже что-то рассказывает.
 *
 * ЗАКОН, по умолчанию для любого будущего места: «?» стоит только там, где по нажатию есть что
 * показать, — карточку коуча этого места, сейчас же, с роликом, когда тот снят. Показывать нечего
 * (нет записи в каталоге, пустая привязка, закрытый шаг флоу) — знака вопроса нет: хост прячет его
 * сам. Наведение с подписью не в счёт — это не содержимое. Хост не меняется ничем, кроме привязки.
 *
 * ЗАКОН МЕСТА (R46, D14/D16): «?» — это и есть место карточки. Значок перевешивает и вход в
 * контейнер, и якорь записи: карточка встаёт на сам нажатый значок, носик упирается в него, а
 * сторону выбирает свободное место — и на своём экране записи, и на любом чужом. Нажатие на «?»
 * карточку не проходит — оно только поднимает и убирает её.
 *
 * ЗАКОН БЕЗ ПОДСКАЗКИ: пока «?» виден, `hlmTooltip` хоста молчит — подсказка читается как ответ,
 * и фотограф наводит, но не нажимает. Шаблоны «?» `hlmTooltip` не ставят вовсе.
 */
@Directive({
  selector: '[onboardingHelp]',
  host: { '(click)': 'request()', '[style.display]': "available() ? null : 'none'" },
})
export class OnboardingHelpDirective {
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  readonly onboardingHelp = input<string | null>(null);

  constructor() {
    muteTooltipWhile(() => this.available());
    effect(onCleanup => {
      const id = this.onboardingHelp();
      if (!id) return;

      const onboarding = this.injector.get(OnboardingService);
      onboarding.registerHelp(id, this.element);
      onCleanup(() => onboarding.unregisterHelp(id, this.element));
    });
  }

  /** Вердикт движка о том же id, который откроет просьба: одно правило показа на «?» и на карточку. */
  protected readonly available = computed(() => {
    const id = this.onboardingHelp();

    return !!id && this.injector.get(OnboardingService).helpAvailable(id);
  });

  protected request(): void {
    const id = this.onboardingHelp();
    if (!id) return;

    const onboarding = this.injector.get(OnboardingService);
    // Нажатый значок забирает место карточки себе: у записи бывает несколько «?», и карточка идёт
    // к тому, по которому нажали, а не к якорю записи где-то ещё на экране (D14/D16).
    onboarding.registerHelp(id, this.element);
    onboarding.requestHint(id);
  }
}

/** Пауза указателя на хосте до карточки и хвост после ухода с хоста и карточки. */
const HOVER_OPEN_MS = 700;
const HOVER_CLOSE_MS = 300;

/**
 * Карточка урока по наведению на сам контрол функции — только там, где продукт назвал это место (иконка
 * «Маски» рейла дизайнера). Контрол остаётся собой: не прячется, нажатие ведёт его обработчик. Пока урок
 * есть, `hlmTooltip` хоста молчит. Карточка встаёт на хост, как у нажатого «?», и уходит через 300 мс после
 * ухода указателя с хоста и карточки — без записи о прохождении. Касание наведения не знает.
 */
@Directive({ selector: '[onboardingHoverHelp]' })
export class OnboardingHoverHelpDirective {
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  readonly onboardingHoverHelp = input<string | null>(null);

  private readonly available = computed(() => {
    const id = this.onboardingHoverHelp();

    return !!id && this.injector.get(OnboardingService).helpAvailable(id);
  });

  private openTimer: ReturnType<typeof setTimeout> | undefined;
  private closeTimer: ReturnType<typeof setTimeout> | undefined;
  /** Id карточки, поднятой этим хостом: снимать при уходе можно только её. */
  private opened: string | null = null;
  /** Место «?», занятое хостом на время карточки: уходя, отдаём его обратно. */
  private lent: HTMLElement | undefined;

  constructor() {
    muteTooltipWhile(this.available);
    this.element.addEventListener('pointerenter', (event: PointerEvent) => {
      clearTimeout(this.closeTimer);
      if (event.pointerType === 'touch' || !this.available()) return;
      this.openTimer = setTimeout(() => this.open(), HOVER_OPEN_MS);
    });
    this.element.addEventListener('pointerleave', () => {
      clearTimeout(this.openTimer);
      this.scheduleClose();
    });
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.release();
    });
  }

  private open(): void {
    const id = this.onboardingHoverHelp();
    if (!id || !this.available()) return;

    const onboarding = this.injector.get(OnboardingService);
    // Карточка уже стоит (тур или «?») — наведение её не трогает: `requestHint` её бы убрал.
    if (onboarding.activeHint()?.record.id === id) return;
    this.lent = onboarding.registerHelp(id, this.element);
    onboarding.requestHint(id);
    this.opened = id;
  }

  private scheduleClose(): void {
    this.closeTimer = setTimeout(() => {
      const card = document.querySelector('onboarding-coach-card');
      if (this.element.matches(':hover') || card?.matches(':hover')) return this.scheduleClose();
      this.release();
    }, HOVER_CLOSE_MS);
  }

  private release(): void {
    const id = this.opened;
    if (!id) return;

    this.opened = null;
    const onboarding = this.injector.get(OnboardingService);
    if (onboarding.requestedHint() === id) onboarding.releaseHint(id);
    onboarding.unregisterHelp(id, this.element, this.lent);
    this.lent = undefined;
  }
}
